import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import axios from "axios";
import { API } from "../App";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Sparkles,
  ArrowRight,
  Users,
  MessageSquare,
  TrendingUp,
  Flame,
  CheckCircle,
  Star,
  Play,
  Shield,
  Clock,
  Zap,
  Activity,
  Dumbbell,
  ChevronRight
} from "lucide-react";

const previewClients = [
  { name: "Ana García", streak: 21, status: "green", avatar: "👩‍💼", lastMsg: "¡Hoy comí muy bien!" },
  { name: "Carlos M.", streak: 14, status: "green", avatar: "👨‍🦱", lastMsg: "Seguí el plan" },
  { name: "María L.", streak: 7, status: "yellow", avatar: "👩‍🦰", lastMsg: "Tuve un antojo..." },
  { name: "Pedro S.", streak: 30, status: "green", avatar: "👨‍💻", lastMsg: "🔥 ¡Mi mejor racha!" },
];

const Login = () => {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [demoLoading, setDemoLoading] = useState(false);
  const [activePreview, setActivePreview] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [selectedMode, setSelectedMode] = useState(null); // 'nutritionist' | 'athlete'

  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => setShowStats(true), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActivePreview((prev) => (prev + 1) % previewClients.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleDemoAccess = async () => {
    setDemoLoading(true);
    try {
      await axios.post(`${API}/seed-demo-data`).catch(() => {});
      const response = await axios.post(`${API}/auth/demo-login`, {}, { withCredentials: true });
      login(response.data);
      navigate("/dashboard");
    } catch (error) {
      navigate("/dashboard");
    } finally {
      setDemoLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F4D40] via-[#134E3E] to-[#0A3A2E] flex items-center justify-center">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-white animate-pulse" />
          </div>
          <p className="text-white/80">Cargando HabitIA...</p>
        </motion.div>
      </div>
    );
  }

  // Mode Selection Screen
  if (!selectedMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F4D40] via-[#134E3E] to-[#0A3A2E] overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div 
            className="absolute top-1/4 -left-20 w-96 h-96 bg-[#178A6B]/20 rounded-full blur-3xl"
            animate={{ scale: [1, 1.2, 1], x: [0, 30, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-1/4 -right-20 w-80 h-80 bg-[#E57A4F]/15 rounded-full blur-3xl"
            animate={{ scale: [1, 1.3, 1], x: [0, -40, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-8">
          {/* Logo */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 mb-12"
          >
            <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <span className="text-3xl font-bold text-white">
              Habit<span className="font-light">IA</span>
            </span>
          </motion.div>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-10"
          >
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              ¿Cómo quieres usar HabitIA?
            </h1>
            <p className="text-white/60 max-w-md">
              Elige tu perfil para una experiencia personalizada
            </p>
          </motion.div>

          {/* Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full max-w-2xl">
            {/* Nutritionist Option */}
            <motion.button
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              onClick={() => setSelectedMode('nutritionist')}
              className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-6 text-left hover:bg-white/15 transition-all group"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                Soy Nutricionista
              </h3>
              <p className="text-white/60 text-sm mb-4">
                Gestiona múltiples clientes con IA que clona tu estilo. Check-ins automáticos por WhatsApp.
              </p>
              <div className="flex items-center gap-2 text-blue-400 text-sm font-medium">
                <span>Desde $29/mes</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.button>

            {/* Athlete Option */}
            <motion.button
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              onClick={() => navigate('/athlete/login')}
              className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-6 text-left hover:bg-white/15 transition-all group"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                <Activity className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                Entreno Solo
              </h3>
              <p className="text-white/60 text-sm mb-4">
                Sé tu propio coach. Sube tu dieta o crea una. Trackea macros y construye hábitos.
              </p>
              <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
                <span>Desde $9/mes</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.button>
          </div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="flex items-center gap-8 mt-12 text-white/50 text-sm"
          >
            <div className="text-center">
              <p className="text-xl font-bold text-white">500+</p>
              <p>Nutricionistas</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-white">2,000+</p>
              <p>Atletas</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-white">94%</p>
              <p>Adherencia</p>
            </div>
          </motion.div>

          {/* Admin Link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-8"
          >
            <button
              onClick={() => navigate('/admin/login')}
              className="text-white/30 text-xs hover:text-white/50 transition-colors flex items-center gap-1"
            >
              <Shield className="w-3 h-3" />
              Admin Panel
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Nutritionist Login
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F4D40] via-[#134E3E] to-[#0A3A2E] overflow-hidden relative">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          className="absolute top-1/4 -left-20 w-96 h-96 bg-[#178A6B]/20 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-1/4 -right-20 w-80 h-80 bg-[#E57A4F]/15 rounded-full blur-3xl"
          animate={{ scale: [1, 1.3, 1], x: [0, -40, 0], y: [0, 30, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white/30 rounded-full"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
            animate={{ y: [0, -100, 0], opacity: [0, 1, 0] }}
            transition={{ duration: 5 + Math.random() * 5, repeat: Infinity, delay: Math.random() * 5, ease: "easeInOut" }}
          />
        ))}
      </div>

      <div className="relative z-10 min-h-screen flex">
        {/* Left side - Login */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-12 lg:px-16 xl:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-md mx-auto lg:mx-0 w-full"
          >
            {/* Back button */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => setSelectedMode(null)}
              className="flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              <span className="text-sm">Cambiar perfil</span>
            </motion.button>

            {/* Logo */}
            <motion.div 
              className="flex items-center gap-3 mb-12"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-2xl font-bold text-white">
                  Habit<span className="font-light">IA</span>
                </span>
                <p className="text-blue-300 text-xs font-medium">Para Nutricionistas</p>
              </div>
            </motion.div>

            {/* Main heading */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-8"
            >
              <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-4">
                Bienvenido de vuelta
              </h1>
              <p className="text-lg text-white/70">
                Accede a tu panel y gestiona tus clientes con el poder de la IA
              </p>
            </motion.div>

            {/* Live stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: showStats ? 1 : 0, y: showStats ? 0 : 20 }}
              transition={{ delay: 0.5 }}
              className="grid grid-cols-3 gap-4 mb-8"
            >
              {[
                { label: "Nutricionistas", value: "500+", icon: Users },
                { label: "Check-ins hoy", value: "2.3k", icon: CheckCircle },
                { label: "Adherencia", value: "94%", icon: TrendingUp },
              ].map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.6 + i * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/10"
                >
                  <stat.icon className="w-4 h-4 text-[#E57A4F] mb-1" />
                  <p className="text-xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs text-white/60">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* Login buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="space-y-4"
            >
              <Button 
                onClick={handleLogin}
                size="lg"
                className="w-full rounded-2xl bg-white text-[#0F4D40] hover:bg-white/90 py-6 text-lg font-semibold shadow-xl shadow-black/20 transition-all hover:shadow-2xl hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuar con Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/20"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-transparent text-white/50">o</span>
                </div>
              </div>

              <Button
                onClick={handleDemoAccess}
                disabled={demoLoading}
                size="lg"
                variant="outline"
                className="w-full rounded-2xl border-white/30 bg-white/5 backdrop-blur-sm text-white hover:bg-white/10 hover:border-white/50 py-6 text-lg font-medium transition-all"
              >
                {demoLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                    Cargando demo...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5 mr-3" />
                    Probar Demo Gratis
                  </>
                )}
              </Button>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mt-8 flex items-center justify-center lg:justify-start gap-6 text-white/50 text-sm"
            >
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>Seguro</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4" />
                <span>Sin tarjeta</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>2 min setup</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Right side - Preview */}
        <div className="hidden lg:flex w-1/2 items-center justify-center p-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, x: 50 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative w-full max-w-lg"
          >
            {/* Main preview card */}
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl overflow-hidden">
              <div className="bg-white/5 px-6 py-4 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#178A6B] rounded-xl flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">Tus Clientes</p>
                      <p className="text-white/60 text-sm">12 activos</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-[#178A6B]/20 px-3 py-1.5 rounded-full">
                    <div className="w-2 h-2 bg-[#178A6B] rounded-full animate-pulse"></div>
                    <span className="text-[#178A6B] text-sm font-medium">En vivo</span>
                  </div>
                </div>
              </div>

              <div className="p-4 space-y-3">
                <AnimatePresence mode="wait">
                  {previewClients.map((client, index) => (
                    <motion.div
                      key={client.name}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ 
                        opacity: 1, 
                        x: 0,
                        scale: activePreview === index ? 1.02 : 1,
                        backgroundColor: activePreview === index ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.05)'
                      }}
                      transition={{ duration: 0.3 }}
                      className="flex items-center justify-between p-3 rounded-xl border border-white/10"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          client.status === 'green' ? 'bg-[#178A6B]' : 
                          client.status === 'yellow' ? 'bg-[#F59E0B]' : 'bg-[#DC2626]'
                        }`}></div>
                        <span className="text-2xl">{client.avatar}</span>
                        <div>
                          <p className="text-white font-medium text-sm">{client.name}</p>
                          <p className="text-white/50 text-xs truncate max-w-[120px]">{client.lastMsg}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 bg-[#E57A4F]/20 px-2.5 py-1 rounded-full">
                        <Flame className="w-3.5 h-3.5 text-[#E57A4F]" />
                        <span className="text-[#E57A4F] font-bold text-sm">{client.streak}</span>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              <div className="px-6 py-4 bg-white/5 border-t border-white/10 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">94%</p>
                  <p className="text-xs text-white/60">Adherencia</p>
                </div>
                <div className="text-center border-x border-white/10">
                  <p className="text-2xl font-bold text-[#E57A4F]">12</p>
                  <p className="text-xs text-white/60">Check-ins hoy</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#178A6B]">0</p>
                  <p className="text-xs text-white/60">En riesgo</p>
                </div>
              </div>
            </div>

            {/* Floating notification */}
            <motion.div
              initial={{ opacity: 0, y: 20, x: 20 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ delay: 1.2 }}
              className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-2xl p-4 max-w-[200px]"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#178A6B]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-[#178A6B]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Nuevo check-in</p>
                  <p className="text-xs text-gray-500">Ana García acaba de reportar ⭐⭐⭐⭐⭐</p>
                </div>
              </div>
            </motion.div>

            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.5, type: "spring" }}
              className="absolute -top-4 -right-4 bg-gradient-to-br from-[#E57A4F] to-[#D4694E] text-white rounded-2xl shadow-xl px-4 py-3"
            >
              <div className="flex items-center gap-2">
                <Flame className="w-5 h-5" />
                <div>
                  <p className="text-lg font-bold">+85%</p>
                  <p className="text-xs text-white/80">Adherencia</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;
